/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */

import { action, computed, observable, when } from 'mobx';
import Fuse from 'fuse.js';
import {
  isEmpty,
  flatMap,
  groupBy,
  mapValues,
  keyBy,
  unionBy,
  flatten,
} from 'lodash';

import { DateTime } from 'luxon';

import {
  LegacyOffering,
  LegacyEnrollmentEtype,
  CatalogEntry,
  CatalogPolicies,
  CourseFormat,
  CourseSubformat,
  CourseType,
  ExamType,
  Modality,
  ModalityExtension,
  OfferingCodePrefix,
  OfferingTypes,
  OfferingVisibility,
  SubscriptionCode,
} from 'types';
import { extractFromSlug } from 'helpers/utils';
import { getCourseTitleByLanguage } from 'services/UtilityService';
import {
  getCatalog as getSubscriptionCatalog,
  getCatalogEntries,
  getCatalogEntriesV2,
  getCatalogPolicyMap,
} from 'services/CatalogService';
import FiltersStore from 'stores/common/Filters';

import type { IRootStore } from 'stores/IRootStore';
import { EnablementFeatureFlags } from 'config/featureFlags';
import {
  getUserAssessments,
  isSkillsAssessmentClientError,
  SkillsAssessment,
} from 'services/SkillsAssessmentService';
import {
  CatalogEntryLink,
  CatalogEntryRoute,
  CatalogFilters,
  GetCatalogEntryRouteOptions,
  SortingType,
} from './types';
import { getFilterConfigurations } from './config';
import {
  getUserPreferences,
  postUserSavedForLaterOffering,
} from '../../services/UserPreferences';

const distantFuture = new Date(8640000000000000);
const distantPast = new Date(-8640000000000000);
const MS_IN_DAY = 24 * 60 * 60 * 1000;
export const NEW_OFFERING_WINDOW_DAYS = 180;

class CatalogStore extends FiltersStore<CatalogEntry> {
  @observable subscriptionCatalog: CatalogEntry[] = [];

  @observable loadedSubscriptionCatalog: boolean = false;

  @observable loadingSubscriptionCatalog: boolean = false;

  @observable allCatalogEntries: CatalogEntry[] = [];

  @observable loadedCatalog: boolean = false;

  @observable loadingCatalog: boolean = false;

  @observable policies: CatalogPolicies = {}; // only defined if feature flag is on for policy based catalog

  @observable loadingPolicies: boolean = false;

  @observable managementOverviewFilters: CatalogFilters; // TODO: move this to a separate store

  @observable searchResults = null;

  @observable _filteredEntries: CatalogEntry[] = [];

  @observable.ref courseCollateral = [];

  @observable _currentPage: number = 1;

  @observable entriesPerPage: number = 20;

  @observable selectedByExternalSubscriber = '';

  @observable sortBy: SortingType = SortingType.Alphabetical;

  /** code → pin timestamp (unix). Populated from API field `favorites`. */
  @observable savedForLater: Record<string, number> = {};

  @observable lastCatalogParams: Record<string, string> = {};

  @observable skillsAssessments: SkillsAssessment[] = [];

  @observable skillsAssessmentsAvailable = false;

  rootStore: IRootStore;

  constructor(rootStore: IRootStore) {
    super(CatalogStore.initializeFilters(), rootStore.routerStore);
    this.rootStore = rootStore;
    this.managementOverviewFilters = CatalogStore.initializeFilters();

    // Feature flags and user subscription must be loaded before we compute the catalog
    when(
      () =>
        rootStore.userStore?.didFetchSubscription &&
        rootStore.featuresStore.allFeaturesFetched,
      () => {
        if (rootStore.userStore.hasActiveSubscription) {
          this.getCurrentSubscriptionCatalog(true);
        } else {
          const policyBasedCatalogEnabled =
            rootStore.featuresStore.isFeatureEnabled(
              'enablements',
              EnablementFeatureFlags.PolicyBasedCatalog,
            );

          if (policyBasedCatalogEnabled) {
            Promise.all([
              this.getCatalogPolicyMap(true),
              this.getAllCatalogEntries(true),
            ]);
          } else {
            this.getAllCatalogEntries(true);
          }
        }
        this.getSkillsAssessments();
      },
    );

    when(
      () => this.loaded && rootStore.userStore.hasActiveSubscription,
      () => {
        this.getSavedForLater();
      },
    );
  }

  @computed get fuseIndex() {
    const options = {
      shouldSort: true,
      threshold: 0,
      tokenize: true,
      matchAllTokens: true,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      keys: ['slug', 'title', 'modality', 'course_type'],
    };
    return new Fuse(this.filteredEntries, options);
  }

  @action searchEntries = (s: string) => {
    if (!s?.length) {
      this.searchResults = this.filteredEntries;
      return;
    }

    this.searchResults = this.fuseIndex.search(s);
  };

  @computed get query() {
    return this.rootStore.searchStore.query;
  }

  @computed get totalPages(): number {
    return Math.ceil(this.filteredEntries.length / this.entriesPerPage) || 1;
  }

  @computed get filteredCount(): number {
    return this.filteredEntries.length || 0;
  }

  @computed get currentPage(): number {
    if (this._currentPage > this.totalPages) {
      return 1;
    }

    return this._currentPage;
  }

  getFilterConfigs = () => {
    // Policy-applied list (`entries`), before sidebar/search filters (`filteredEntries`).
    const showMyOfferingsFilter = this.entries.some(
      (entry) => entry.visibility === OfferingVisibility.Upgrade,
    );

    return getFilterConfigurations(
      showMyOfferingsFilter,
      CatalogStore.isCompReview,
      this.rootStore.uiStore?.tFunction,
    );
  };

  /**
   * Returns an object where each key is a course code
   * and the value is the catalog entry of the latest version.
   * This is useful for displaying a list of unique courses.
   * Only works on subscription based catalog.
   *
   * @visibility RAW - Policies NOT applied.
   * @versions LATEST_ONLY - One entry per course code.
   */
  @computed get uniqueCatalogEntries(): Record<string, CatalogEntry> {
    // TODO: analyse the possibility of moving this to service layer where we get subscription catalog.
    // we are returning a new object every time, which could cause unnecessary re-renders in React components.

    const filteredCatalog = this.subscriptionCatalog.filter(
      (entry) =>
        entry.modality !== Modality.VideoClassroom &&
        entry.visibility !== OfferingVisibility.Hidden,
    );

    const translations =
      CatalogStore.collectTranslationsByCode(filteredCatalog);

    // Construct a dict of unique catalog entries, preferring higher visibility (Visible > Upgrade > Hidden), then latest version
    const uniqueDict = filteredCatalog.reduce((dict, entry) => {
      const existing = dict[entry.code];

      if (!existing) {
        dict[entry.code] = entry;
        return dict;
      }

      // 1 - Prioritize visibility: Visible > Upgrade > Hidden (hidden ones were already filtered out)
      // Higher visibility always wins, regardless of version
      if (CatalogStore.hasHigherVisibility(entry, existing)) {
        dict[entry.code] = entry;
        return dict;
      }
      if (CatalogStore.hasHigherVisibility(existing, entry)) {
        return dict;
      }

      // 2 - Only if visibility is equal, fall back to version comparison to prioritize the more recent version
      const existingEpoch = existing.epoch_number ?? 0;
      const newEpoch = entry.epoch_number ?? 0;

      if (newEpoch > existingEpoch) {
        dict[entry.code] = entry;
      } else if (newEpoch === existingEpoch) {
        // Fall back to version comparison if epochs are equal
        if (CatalogStore.isGreaterVersion(entry.version, existing.version)) {
          dict[entry.code] = entry;
        }
      }
      return dict;
    }, {});

    const newUniqueDict = Object.fromEntries(
      Object.entries(uniqueDict).map(([code, entry]: any) => [
        code,
        { ...entry, translations: translations[entry.code] || [] },
      ]),
    );

    return newUniqueDict;
  }

  /**
   * The catalog entries to which the user has access to via the UI.
   * For subscription users this will be the subscription catalog.
   * For alacarte this is the enrolled offerings.
   * The combination of both is also considered here.
   *
   * @visibility POLICY_APPLIED - This is the source of truth for visibility.
   * @versions LATEST_ONLY - One entry per course code.
   */
  @computed get entries(): Array<CatalogEntry> {
    const policyBasedCatalogEnabled =
      this.rootStore.featuresStore.isFeatureEnabled(
        'enablements',
        EnablementFeatureFlags.PolicyBasedCatalog,
      );
    const allowedEntries = this.uniqueCatalogEntries; // empty if no subscription

    if (this.rootStore?.userStore.hasAlaCarteEnrollments) {
      Object.values(this.groupedLegacyOfferings).forEach(
        (enrollmentOffering) => {
          // 1. add entry if subscription catalog does not contain it
          if (!allowedEntries[enrollmentOffering.code]) {
            const enrollmentEntry = this.allCatalogEntries.find(
              (entry) => entry.slug === enrollmentOffering.slug,
            );
            if (enrollmentEntry) {
              allowedEntries[enrollmentOffering.code] = enrollmentEntry;
            }
          }

          // 2. Make sure entry is visible:
          if (allowedEntries[enrollmentOffering.code]?.doc_type) {
            allowedEntries[enrollmentOffering.code] = {
              ...allowedEntries[enrollmentOffering.code],
              visibility: OfferingVisibility.Visible,
            };
          }

          const relatedEnrollment =
            this.rootStore.classesStore?.enrollmentsBySlugTag?.[
              enrollmentOffering?.slug_tag
            ];

          // 3. For RHLS course enrollments, set the visibility of the dependents:
          if (
            policyBasedCatalogEnabled &&
            relatedEnrollment?.etype === LegacyEnrollmentEtype.RHLSCourse
          ) {
            CatalogStore.handleRhlsCourseVisibilities(
              enrollmentOffering,
              allowedEntries,
              this.allUniqueCatalogEntries,
              this.policies,
            );
          }
        },
      );
    }
    return Object.values(allowedEntries);
  }

  /**
   * Index of entries by code for O(1) lookup.
   * Derived from `entries`, so visibility is correct.
   *
   * @visibility POLICY_APPLIED - Derived from `entries`.
   * @versions LATEST_ONLY - One entry per course code.
   */
  @computed get entriesByCode(): Record<string, CatalogEntry> {
    return this.entries.reduce((acc, entry) => {
      acc[entry.code.toLowerCase()] = entry;
      return acc;
    }, {} as Record<string, CatalogEntry>);
  }

  /**
   * Same as uniqueCatalogEntries, but does it with all the available catalog entries.
   * Useful mostly for cases in which the user doesn't have an active subscription,
   * such as for managers, alacarte and open users, or text searches.
   *
   * @visibility RAW - Policies NOT applied.
   * @versions LATEST_ONLY - One entry per course code.
   */
  @computed get allUniqueCatalogEntries(): Record<string, CatalogEntry> {
    const filteredCatalog = this.allCatalogEntries.filter(
      (entry) =>
        entry.modality !== Modality.VideoClassroom &&
        entry.visibility !== OfferingVisibility.Hidden,
    );

    const translations =
      CatalogStore.collectTranslationsByCode(filteredCatalog);

    const uniqueDict = filteredCatalog.reduce((dict, entry) => {
      if (
        !dict[entry.code] ||
        CatalogStore.isGreaterVersion(entry.version, dict[entry.code].version)
      ) {
        dict[entry.code] = entry;
      }
      return dict;
    }, {});

    Object.entries(uniqueDict).forEach(([code, entry]: any) => {
      uniqueDict[code] = {
        ...entry,
        translations: translations[entry.code] || [],
      };
    });

    return uniqueDict;
  }

  set entries(entries) {
    this.subscriptionCatalog = entries;
  }

  /**
   * @visibility POLICY_APPLIED - Derived from `entries`.
   * @versions LATEST_ONLY - One entry per course code.
   */
  @computed get filteredEntries() {
    const checkedFiltersLength = flatMap(
      this.filterKeys.map((key) =>
        this.filters[key].filter((filter) => filter.checked),
      ),
    ).length;

    return checkedFiltersLength || this.query
      ? this._filteredEntries
      : this.entries;
  }

  /**
   * Recalculates sidebar filter counts for the catalog.
   * Delegates to {@link FiltersStore.recalculateFilterCountsFromEntries},
   * applying the current search query when one is active.
   */
  @action recalculateFilterCounts = () => {
    this.recalculateFilterCountsFromEntries(this.entries, (entries) => {
      if (!this.query) {
        return entries;
      }

      const options = {
        shouldSort: true,
        threshold: 0,
        tokenize: true,
        matchAllTokens: true,
        maxPatternLength: 32,
        minMatchCharLength: 1,
        keys: ['slug', 'title', 'modality', 'course_type'],
      };

      return new Fuse(entries, options).search(this.query);
    });
  };

  @action updateSorting = (type: SortingType) => {
    this.sortBy = type;
  };

  /**
   * @visibility POLICY_APPLIED - Derived from `filteredEntries`.
   * @versions LATEST_ONLY - One entry per course code.
   */
  @computed get paginatedEntries(): CatalogEntry[] {
    const startIndex = (this.currentPage - 1) * this.entriesPerPage;

    let sortedEntries = [];

    // adding translated titles to the entries
    const processedEntries = this.filteredEntries.map((e) => ({
      translatedTitle: CatalogStore.getTitleWithFallback(
        e,
        this.rootStore.uiStore.currentLanguage,
      ),
      catalogEntry: e,
    }));

    switch (this.sortBy) {
      case SortingType.RevAlphabetical:
        sortedEntries = processedEntries.sort((a, b) =>
          b.translatedTitle.localeCompare(a.translatedTitle),
        );
        break;
      case SortingType.Newest:
        sortedEntries = processedEntries.sort((a, b) => {
          const dateA = a.catalogEntry.sort_date
            ? new Date(a.catalogEntry.sort_date)
            : distantPast;
          const dateB = b.catalogEntry.sort_date
            ? new Date(b.catalogEntry.sort_date)
            : distantPast;

          return dateB.getTime() - dateA.getTime();
        });
        break;
      case SortingType.Oldest:
        sortedEntries = processedEntries.sort((a, b) => {
          const dateA = a.catalogEntry.sort_date
            ? new Date(a.catalogEntry.sort_date)
            : distantFuture;
          const dateB = b.catalogEntry.sort_date
            ? new Date(b.catalogEntry.sort_date)
            : distantFuture;

          return dateA.getTime() - dateB.getTime();
        });
        break;
      case SortingType.SKUAlphabetical:
        sortedEntries = processedEntries.sort((a, b) =>
          (a.catalogEntry.code || '').localeCompare(b.catalogEntry.code || ''),
        );
        break;
      case SortingType.SKURevAlphabetical:
        sortedEntries = processedEntries.sort((a, b) =>
          (b.catalogEntry.code || '').localeCompare(a.catalogEntry.code || ''),
        );
        break;
      case SortingType.Alphabetical:
      default:
        sortedEntries = processedEntries.sort((a, b) =>
          a.translatedTitle.localeCompare(b.translatedTitle),
        );
        break;
    }

    return sortedEntries
      .map((e) => e.catalogEntry)
      .slice(startIndex, startIndex + this.entriesPerPage);
  }

  /**
   * Filters the catalog entries based on the checked filters.
   * If a query is present, it will also filter the entries based on the query.
   */
  @action filterEntries = () => {
    this._filteredEntries = FiltersStore.getFilteredEntries<CatalogEntry>(
      this.entries,
      this.getFilterConfigs(),
      this.filters,
    );

    if (this.query) {
      this.searchEntries(this.query);
      this._filteredEntries = this.searchResults;
    }
  };

  @action getCatalogPolicyMap = async (force: boolean = false) => {
    if (!isEmpty(this.policies) && !force) {
      return this.policies;
    }

    let policies: CatalogPolicies = {};

    try {
      this.loadingPolicies = true;
      policies = await getCatalogPolicyMap();
      this.policies = policies;
    } catch (e) {
      console.error(e);
    }
    this.loadingPolicies = false;
    return policies;
  };

  /**
   * Fetches the catalog entries for a given subscription.
   * @param {string} subscriptionCode - the subscription code to fetch the catalog entries for
   * @param {boolean} force - if true, will fetch the catalog entries even if they are already loaded
   */
  @action getCatalogEntriesBySubscrition = async (
    subscriptionCode: string,
    force: boolean,
  ) => {
    if (this.loadedSubscriptionCatalog && !force) {
      return this.subscriptionCatalog;
    }

    if (!subscriptionCode) {
      return this.subscriptionCatalog;
    }

    this.loadingSubscriptionCatalog = true;
    try {
      const policyBasedCatalogEnabled =
        this.rootStore.featuresStore.isFeatureEnabled(
          'enablements',
          EnablementFeatureFlags.PolicyBasedCatalog,
        );

      let subscriptionEntries: CatalogEntry[] = [];

      if (policyBasedCatalogEnabled) {
        // Run both API calls in parallel
        const [policies] = await Promise.all([
          this.getCatalogPolicyMap(),
          this.getAllCatalogEntries(force),
        ]);

        subscriptionEntries = CatalogStore.applyPolicies(
          this.allCatalogEntries,
          this.rootStore.userStore.currentSubscriptionCode,
          policies,
        );
      } else {
        const [, entries] = await Promise.all([
          this.getAllCatalogEntries(force),
          getSubscriptionCatalog(subscriptionCode),
        ]);

        subscriptionEntries = entries;
      }

      if (subscriptionEntries) {
        CatalogStore.sortExpertSeminars(subscriptionEntries);
        this.subscriptionCatalog = subscriptionEntries;
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.loadingSubscriptionCatalog = false;
      this.loadedSubscriptionCatalog = true;
    }

    return this.subscriptionCatalog;
  };

  /**
   * Fetches the catalog entries for the current subscription.
   * @param {boolean} force - if true, will fetch the catalog entries even if they are already loaded
   */
  @action getCurrentSubscriptionCatalog = async (
    force?: boolean,
  ): Promise<Array<CatalogEntry>> =>
    this.getCatalogEntriesBySubscrition(
      this.rootStore.userStore.currentSubscriptionCode,
      force,
    );

  /**
   * Fetches all catalog entries, independent from any subscription. Usually used for, but not limited to, alacarte users.
   * @param {*} force - if true, will fetch the catalog entries even if they are already loaded
   */
  @action getAllCatalogEntries = async (
    force?: boolean,
  ): Promise<Array<CatalogEntry>> => {
    if (!this.rootStore?.featuresStore.allFeaturesFetched) {
      return [];
    }

    if (this.allCatalogEntries.length && !force) {
      return this.allCatalogEntries;
    }

    this.loadingCatalog = true;

    try {
      const policyBasedCatalogEnabled =
        this.rootStore.featuresStore.isFeatureEnabled(
          'enablements',
          EnablementFeatureFlags.PolicyBasedCatalog,
        );

      const targetAPI = policyBasedCatalogEnabled
        ? getCatalogEntriesV2
        : getCatalogEntries;
      const allCatalogEntries: Array<CatalogEntry> = await targetAPI();
      if (allCatalogEntries) {
        this.allCatalogEntries = allCatalogEntries;
      }
    } finally {
      this.loadingCatalog = false;
      this.loadedCatalog = true;
    }

    return this.allCatalogEntries;
  };

  @action async getSavedForLater() {
    try {
      const preferences = await getUserPreferences();
      // API response field remains `favorites` for compatibility.
      const { favorites } = preferences.data;
      this.savedForLater = Object.fromEntries(
        flatten(Object.values(favorites || {})).map(({ code, timestamp }) => [
          code,
          CatalogStore.toUnixTimestamp(timestamp),
        ]),
      );
    } catch {
      this.savedForLater = {};
    }
    return this.savedForLater;
  }

  @action async postSavedForLater(
    offeringCode: string,
    modality: Modality,
    refresh = false,
  ) {
    const response = await postUserSavedForLaterOffering(
      offeringCode,
      modality,
      refresh,
    );
    // API response field remains `favorites` for compatibility.
    const { favorites } = response.data;
    this.savedForLater = Object.fromEntries(
      flatten(Object.values(favorites || {})).map(({ code, timestamp }) => [
        code,
        CatalogStore.toUnixTimestamp(timestamp),
      ]),
    );

    // Turn ON must win over last access even when progress @timestamp is
    // timezone-naive (parsed as local) or clocks drift across services.
    if (refresh) {
      const lastAccess = this.getLastAccessUnix(offeringCode) ?? 0;
      const pinnedAt = this.savedForLater[offeringCode] ?? 0;
      this.savedForLater[offeringCode] = Math.max(
        pinnedAt,
        lastAccess,
        DateTime.utc().toUnixInteger(),
      );
    }

    return this.savedForLater;
  }

  /**
   * Parse API/ES datetimes as UTC when they lack an offset (progress docs often do).
   */
  static toUnixTimestamp(value?: string | null) {
    if (!value) {
      return Number.NaN;
    }
    return DateTime.fromISO(value, { zone: 'utc' }).toUnixInteger();
  }

  getLastAccessUnix(code: string) {
    const progress = this.rootStore.progressStore?.progressByCourse?.find(
      (record) => record.course_code === code,
    );
    if (!progress?.['@timestamp']) {
      return undefined;
    }
    const unix = CatalogStore.toUnixTimestamp(progress['@timestamp']);
    return Number.isFinite(unix) ? unix : undefined;
  }

  /**
   * True when the offering has been started (progress > 0).
   */
  hasStartedOffering(code: string) {
    const progress = this.rootStore.progressStore?.progressByCourse?.find(
      (record) => record.course_code === code,
    );
    return Boolean(progress && progress.total_progress > 0);
  }

  /**
   * Effective Saved-for-later state. Lane exclusivity (one card → one lane):
   * - Not started, not pinned → New
   * - Started, not effectively pinned → Continue learning
   * - Effectively pinned (with or without prior progress) → Saved for later
   * - Effectively pinned, then started again (last access > pin) → Continue learning
   */
  isSavedForLater(code: string) {
    const savedTimestamp = this.savedForLater[code];
    if (savedTimestamp === undefined) {
      return false;
    }

    if (!this.hasStartedOffering(code)) {
      return true;
    }

    const lastAccessTimestamp = this.getLastAccessUnix(code);
    if (lastAccessTimestamp === undefined) {
      return true;
    }

    // >= so a just-pinned offering wins when pin and last access share the same second
    return savedTimestamp >= lastAccessTimestamp;
  }

  @computed get savedForLaterCatalogEntries() {
    return Object.keys(this.savedForLater)
      .filter((code) => this.isSavedForLater(code))
      .map((code) => this.uniqueCatalogEntries[code])
      .filter(Boolean)
      .sort(
        (offeringA, offeringB) =>
          this.savedForLater[offeringB.code] -
          this.savedForLater[offeringA.code],
      );
  }

  /**
   * New lane: newest catalog entries published within the last 180 days
   * that are neither started nor effectively pinned.
   * Sorted by sort_date descending (newest first).
   */
  @computed get newOfferingsCatalogEntries() {
    const cutoff = Date.now() - NEW_OFFERING_WINDOW_DAYS * MS_IN_DAY;

    return [...this.entries]
      .filter((entry): entry is CatalogEntry & { sort_date: string } => {
        if (!entry.sort_date || new Date(entry.sort_date).getTime() < cutoff) {
          return false;
        }

        return (
          !this.isSavedForLater(entry.code) &&
          !this.hasStartedOffering(entry.code)
        );
      })
      .sort(
        (offeringA, offeringB) =>
          new Date(offeringB.sort_date).getTime() -
          new Date(offeringA.sort_date).getTime(),
      );
  }

  /**
   * Returns a dictionary of all catalog entries grouped by course code and version.
   * Works for alacarte users.
   *
   * @visibility RAW - Policies NOT applied. Use `getCatalogEntriesForCode()` for correct visibility.
   * @versions ALL - Contains all versions per course code.
   */
  @computed get groupedAllCatalogEntries() {
    return CatalogStore.composeCatalogGroups(this.allCatalogEntries);
  }

  @action getSkillsAssessments = async () => {
    try {
      const assessments = await getUserAssessments(
        this.rootStore?.userStore.username,
      );
      this.skillsAssessments = assessments;
      this.skillsAssessmentsAvailable = true;
    } catch (error) {
      this.skillsAssessments = [];
      this.skillsAssessmentsAvailable = false;
      if (!isSkillsAssessmentClientError(error)) {
        console.error(error);
      }
    }
  };

  @action getSkillsAssessmentById = (assessmentId: string) =>
    this.skillsAssessments.find((assessment) => assessment.id === assessmentId);

  /**
   * load the filters for the catalog entries. This is used to populate the filters in the UI.
   * This loads the vocabulary data for each filter type and then renders the filters based on
   * what is available in the the catalog entries. This ensures that filters only show up if they
   * exist in at least one catalog entry.
   */
  @action getFilterInfo = async (
    entryList: Array<CatalogEntry> = this.entries || [],
    isManagementOverview: boolean = false,
  ) => {
    const fieldName = isManagementOverview
      ? 'managementOverviewFilters'
      : 'filters';
    return this.__getFilterInfo(
      this.rootStore.uiStore.currentLanguage,
      this.rootStore.vocabularyStore,
      entryList,
      fieldName,
    );
  };

  @action setCurrentPage = (page = 1) => {
    this._currentPage = page;
  };

  @action clearCatalog = () => {
    this.subscriptionCatalog = [];
  };

  @action setLastCatalogParams = (params: Record<string, string>) => {
    this.lastCatalogParams = params;
  };

  navigateToCatalog = () => {
    this.rootStore.routerStore.navigate('catalog', this.lastCatalogParams);
  };

  /**
   * Returns a dictionary of all catalog entries grouped by course code and version.
   * Subscription users only.
   *
   * @visibility RAW - Policies NOT applied. Use `getCatalogEntriesForCode()` for correct visibility.
   * @versions ALL - Contains all versions per course code.
   */
  @computed get groupedCatalogEntries(): Record<
    string,
    Record<string, CatalogEntry>
  > {
    return CatalogStore.composeCatalogGroups(this.subscriptionCatalog);
  }

  subscriptionHasCourseType = (courseType: CourseType) =>
    (this.subscriptionCatalog?.find(
      (entry) =>
        entry?.course_type === courseType &&
        entry?.visibility !== OfferingVisibility.Hidden,
    ) &&
      true) ||
    false;

  @computed get subscriptionHasLabPlus() {
    return this.subscriptionHasCourseType(CourseType.LabPlus);
  }

  @computed get subscriptionHasLesson() {
    return this.subscriptionHasCourseType(CourseType.Lesson);
  }

  @computed get groupedLegacyOfferings(): LegacyOffering[] {
    const { classesStore } = this.rootStore;

    const uniqueDict = Object.values(classesStore.offerings)
      .filter(
        (entry: any) =>
          entry.modality !== Modality.VideoClassroom &&
          entry.visibility !== OfferingVisibility.Hidden,
      )
      .reduce((dict: any, entry: any) => {
        if (
          !dict[entry.slug] ||
          CatalogStore.isGreaterVersion(entry.version, dict[entry.slug].version)
        ) {
          dict[entry.slug] = entry;
        }
        return dict;
      }, {});

    return Object.values(uniqueDict);
  }

  @computed get groupedLegacyOfferingsByCode(): Record<
    string,
    Record<string, LegacyOffering>
  > {
    return mapValues(groupBy(this.groupedLegacyOfferings, 'code'), (o) =>
      keyBy(o, 'version'),
    );
  }

  @computed get loading(): boolean {
    if (
      !this.rootStore.userStore?.didFetchSubscription ||
      !this.rootStore.featuresStore.allFeaturesFetched
    ) {
      return true;
    }

    const policyBasedCatalogEnabled =
      this.rootStore.featuresStore.isFeatureEnabled(
        'enablements',
        EnablementFeatureFlags.PolicyBasedCatalog,
      );

    if (policyBasedCatalogEnabled) {
      return (
        this.loadingCatalog ||
        this.loadingSubscriptionCatalog ||
        this.loadingPolicies
      );
    }

    return this.loadingCatalog || this.loadingSubscriptionCatalog;
  }

  /**
   * Use this over other "loaded" attriutes.
   */
  @computed get loaded(): boolean {
    if (this.rootStore.userStore.hasActiveSubscription) {
      return this.loadedSubscriptionCatalog;
    }
    return this.loadedCatalog;
  }

  @action removeModalityFromCatalog(modalities: string[]) {
    if (!modalities) return;

    this.subscriptionCatalog = this.subscriptionCatalog?.filter(
      (entry) => !modalities.includes(entry.modality),
    );
  }

  @action getSortedVersions(code: string) {
    /**
     * We use a union of entry versions in this,subscriptionCatalog
     * and in this.entries. This way we guarantee that alacarte enrollments
     * that are not in subscription catalog will also be considered.
     * this.entries cannot be used alone because it only contains the latest versions.
     */
    const consideredList = unionBy(
      this.subscriptionCatalog,
      this.entries,
      'slug',
    );

    return consideredList
      .filter((a) => a.code === code)
      .map((a) => a.version)
      .sort((a, b) => {
        const aa = a
          .split('.')
          .map((n) => +n + 100000)
          .join('.');
        const bb = b
          .split('.')
          .map((n) => +n + 100000)
          .join('.');
        if (aa < bb) return -1;
        if (aa > bb) return 1;
        return 0;
      });
  }

  @action getTranslations(code: string) {
    const langs = this.subscriptionCatalog
      .filter((entry) => entry.code === code)
      .map((entry) => entry.language);
    return Array.from(new Set(langs)).sort();
  }

  @action getLatestVersion(code: string) {
    const versions = this.getSortedVersions(code);
    if (!versions.length) {
      return null;
    }

    return versions.slice(-1)[0];
  }

  getTranslatedCourseName(
    slugOrCode: string,
    locale = 'en',
    uniqueCatalogEntries = this.uniqueCatalogEntries,
  ) {
    const { code } = extractFromSlug(slugOrCode);
    const entry = uniqueCatalogEntries[code];
    const translatedName = entry?.translations?.find(
      (element) => element.language === locale,
    )?.title;

    return translatedName || entry?.title || code;
  }

  /**
   * Given a course code, returns any valid full slug (code + version) for the respective entry.
   * Useful for when we need the slug for linking to a course, but we don't know the version.
   * Does not guarantee the highest version.
   * @param {*} code course code without version
   * @param {*} uniqueCatalogEntries
   * @returns course slug (code + version)
   */
  getAnySlugFromCode(
    code: string,
    uniqueCatalogEntries = this.uniqueCatalogEntries,
  ) {
    if (code.includes('-')) {
      return code;
    }

    const entry = uniqueCatalogEntries[code];

    return entry.slug;
  }

  @computed get hasCatalogLoaded() {
    return !(
      isEmpty(this.groupedCatalogEntries) &&
      isEmpty(this.groupedAllCatalogEntries)
    );
  }

  /**
   * Returns a list of catalog entries that are available that have a tier defined and are visible.
   */
  @computed get AvailableToMeEntryProgressPair(): CatalogEntry[] {
    const { progressDict } = this.rootStore.progressStore;

    // filter only visible RHLSTrial courses
    const filtered =
      this.entries?.filter?.(
        (entry) =>
          entry.tier && entry.visibility === OfferingVisibility.Visible,
      ) || [];

    // check progress for each offering
    const pairList: CatalogEntry[] = filtered.filter((entry) => {
      // filter the offering if progress is > 0
      if (progressDict && progressDict[entry.slug]) {
        return false;
      }

      return true;
    });

    return pairList;
  }

  /**
   * Returns all catalog entries (all versions) for a given course code.
   * Tries subscription catalog first, falls back to all catalog entries.
   * Applies visibility corrections from the final `entries` computed property.
   * Useful for components that need to display version dropdowns.
   *
   * @visibility POLICY_APPLIED - Visibility corrected via `entriesByCode` lookup.
   * @versions ALL - Contains all versions for the given course code.
   */
  getCatalogEntriesForCode(code: string): Record<string, CatalogEntry> {
    const normalizedCode = `${code}`.toLowerCase();
    const rawEntries =
      this.groupedCatalogEntries?.[normalizedCode] ||
      this.groupedAllCatalogEntries?.[normalizedCode] ||
      {};

    // lookup for correct visibility from computed index
    const allowedEntry = this.entriesByCode[normalizedCode];
    if (!allowedEntry) {
      return rawEntries;
    }

    // Apply the correct visibility to all versions
    // this is needed because the groupedCatalogEntries and groupedAllCatalogEntries do not have policies applied to them
    return Object.fromEntries(
      Object.entries(rawEntries).map(([version, entry]) => [
        version,
        { ...entry, visibility: allowedEntry.visibility },
      ]),
    );
  }

  /**
   * Returns a catalog entry for a course code and version.
   * By default this will return the entry even if the user does not have access to.
   * If allowedOnly is true, only returns the entry if the code is in the
   * policy-applied catalog (`entries` / `entriesByCode`).
   *
   * Version data comes from raw grouped catalogs; when the code is present in
   * the processed catalog, visibility is stamped from `entriesByCode`.
   *
   * @visibility POLICY_APPLIED when code is in `entriesByCode`; otherwise RAW
   *   (and `undefined` when `allowedOnly` is true).
   */
  getCatalogEntryForCourse(
    courseCode: string,
    courseVersion: string,
    allowedOnly: boolean = false,
  ) {
    const code = `${courseCode}`.toLowerCase();
    const allowedEntry = this.entriesByCode[code];

    if (allowedOnly && !allowedEntry) {
      return undefined;
    }

    // Version lookup uses raw grouped catalogs (policies not applied there)
    const fromSubscription =
      this.groupedCatalogEntries?.[code]?.[courseVersion];
    const fromAlacarte = this.groupedAllCatalogEntries?.[code]?.[courseVersion];

    let entry: CatalogEntry | undefined;
    if (fromSubscription && fromAlacarte) {
      entry = CatalogStore.hasHigherVisibility(fromSubscription, fromAlacarte)
        ? fromSubscription
        : fromAlacarte;
    } else {
      entry = fromSubscription || fromAlacarte;
    }

    if (!entry) {
      return undefined;
    }

    // Stamp processed-catalog visibility when the user can access this code
    if (allowedEntry) {
      return { ...entry, visibility: allowedEntry.visibility };
    }

    return entry;
  }

  /**
   * Returns true if e1 has higher visibility than e2.
   */
  private static hasHigherVisibility(
    e1: CatalogEntry,
    e2: CatalogEntry,
  ): boolean {
    // OfferingVisibility.Visible > OfferingVisibility.Upgrade > OfferingVisibility.Hidden
    const visibilityRank = {
      [OfferingVisibility.Visible]: 3,
      [OfferingVisibility.Upgrade]: 2,
      [OfferingVisibility.Hidden]: 1,
    };

    return visibilityRank[e1.visibility] > visibilityRank[e2.visibility];
  }

  private static initializeFilters(): CatalogFilters {
    return {
      earlyAccess: [],
      languages: [],
      deliveryFormats: [],
      roles: [],
      taxonomyProducts: [],
      taxonomyAudiences: [],
      taxonomyTopics: [],
      level: [],
    };
  }

  private static sortExpertSeminars(catalogEntries: CatalogEntry[]) {
    catalogEntries.sort((itemA, itemB) => {
      if (
        itemA.modality !== Modality.ExpertSeminar ||
        itemB.modality !== Modality.ExpertSeminar
      )
        return 0;

      const valueA = +itemA.code.slice(4);
      const valueB = +itemB.code.slice(4);

      const result = valueB - valueA;

      return result || 0;
    });
  }

  /**
   * Based on a set of policies, return a new list of entries with their visibility properly set.
   * Catalog entries that are already `hidden` (e.g. retired) stay hidden: policy must not override,
   * matching legacy catalog generation which omits such rows from the subscription catalog.
   */
  static applyPolicies(
    catalogEntries: CatalogEntry[],
    subscriptionCode: string,
    policies: CatalogPolicies = {},
  ): CatalogEntry[] {
    if (!Array.isArray(catalogEntries)) {
      return [];
    }
    if (!subscriptionCode) {
      return catalogEntries;
    }
    return catalogEntries.map((e) => {
      if (e.visibility === OfferingVisibility.Hidden) {
        return { ...e, visibility: OfferingVisibility.Hidden };
      }
      const newVisibility =
        policies[e.slug]?.[subscriptionCode] || e.visibility;
      return {
        ...e,
        visibility: newVisibility,
      };
    });
  }

  /**
   * Based on an rhls course enrollment:
   * 1. If entry is visible for premium but
   * set the visibility of its dependents to visible.
   * This method mutates the allowedEntries record.
   */
  static handleRhlsCourseVisibilities(
    enrollment: LegacyOffering,
    allowedEntries: Record<string, CatalogEntry>,
    allUniqueCatalogEntries: Record<string, CatalogEntry>,
    policies: CatalogPolicies,
    referenceSubscription = SubscriptionCode.Premium,
  ) {
    Object.entries(allUniqueCatalogEntries).forEach(([code, entry]) => {
      const isVisibleOnPremium =
        policies[entry.slug]?.[referenceSubscription] ===
        OfferingVisibility.Visible;

      // 1. add missing entries as locked.
      // Only the ones that are hidden for rhlscourse and visible premium.
      // If they are hidden on premium, then they should remain hidden.
      if (
        (!allowedEntries[code] ||
          allowedEntries[code]?.visibility === OfferingVisibility.Hidden) &&
        isVisibleOnPremium
      ) {
        allowedEntries[code] = {
          ...entry,
          visibility: OfferingVisibility.Upgrade,
        };
      }

      // 2. set dependents to visible
      if (CatalogStore.isDependent(enrollment, entry)) {
        allowedEntries[code] = {
          ...entry,
          visibility: OfferingVisibility.Visible,
        };
      }
    });
    return allowedEntries;
  }

  private static isDependent(
    enrollment: LegacyOffering,
    entry: CatalogEntry,
  ): boolean {
    if (
      entry.modality === Modality.ExpertSeminar &&
      entry.related_courses?.includes(enrollment.code)
    ) {
      return true;
    }

    // all exams and tech overviews are automatically visible
    if ([Modality.Exam, Modality.TechOverview].includes(entry.modality)) {
      return true;
    }

    return false;
  }

  static isGreaterVersion(v1: string, v2: string) {
    return (
      v1
        .split('.')
        .map((n) => +n + 100000)
        .join('.')
        .localeCompare(
          v2
            .split('.')
            .map((n) => +n + 100000)
            .join('.'),
        ) > 0
    );
  }

  /**
   * Translations may lag behind the latest course/lesson version, so the
   * winning entry for a code (picked by visibility/version in
   * `uniqueCatalogEntries`/`allUniqueCatalogEntries`) may not itself carry
   * translations even though an older version of the same code does. Also,
   * the a la carte catalog (`getCatalogEntriesV2`/`allCatalogEntries`) has no
   * `translations` field at all - each language is its own flat entry there.
   * This collects every translation available across all versions of each
   * code, from both `entry.translations` and each entry's own `language`,
   * deduped by language, so a course still lists a language once any
   * version/entry of it has been translated.
   */
  static collectTranslationsByCode(
    entries: CatalogEntry[],
  ): Record<string, { language: string; title: string }[]> {
    const translationsByCode: Record<
      string,
      Map<string, { language: string; title: string }>
    > = {};

    entries.forEach((entry) => {
      const map = translationsByCode[entry.code] ?? new Map();
      entry.translations?.forEach((translation) =>
        map.set(translation.language, translation),
      );
      if (entry.language) {
        map.set(entry.language, {
          language: entry.language,
          title: entry.title,
        });
      }
      translationsByCode[entry.code] = map;
    });

    return mapValues(translationsByCode, (map) => Array.from(map.values()));
  }

  /**
   * Composes a dictionary of all catalog entries grouped by course code and then version.
   * @param catalogEntries Array of catalog entries
   */
  static composeCatalogGroups(
    catalogEntries: CatalogEntry[],
  ): Record<string, Record<string, CatalogEntry>> {
    // Construct a dict of all offerings by course code and version, omitting video classrooms
    const versionedEntries = catalogEntries
      .filter(
        (entry) =>
          entry.modality !== Modality.VideoClassroom &&
          entry.visibility !== OfferingVisibility.Hidden,
      )
      .reduce((dict, entry) => {
        // Create a new object for the entry if it doesn't exist
        if (!dict[entry.code]) {
          dict[entry.code] = {};
        }

        // If the version doesn't exist, simply add the entry
        if (!dict[entry.code][entry.version]) {
          dict[entry.code][entry.version] = entry;
        } else {
          // If the version exists, create a new translations array
          const existingTranslations =
            dict[entry.code][entry.version].translations || [];
          const newTranslations = [
            {
              language: entry.language,
              title: entry.title,
            },
            ...existingTranslations,
          ];

          // Create a new CatalogEntry object with the merged translations
          dict[entry.code][entry.version] = {
            ...dict[entry.code][entry.version],
            translations: newTranslations,
          };
        }

        return dict;
      }, {});

    // deepmerge courses
    return versionedEntries;
  }

  static isLocked(offeringVisibility: OfferingVisibility) {
    return (
      offeringVisibility === OfferingVisibility.Upgrade ||
      offeringVisibility === OfferingVisibility.Hidden
    );
  }

  static isEarlyAccess(offeringCode: string) {
    return !!offeringCode?.endsWith(OfferingCodePrefix.EarlyAccess);
  }

  static isRegularCourse(offeringType: CourseType) {
    return offeringType === CourseType.Regular;
  }

  static isLabPlus(offeringType: CourseType) {
    return offeringType === CourseType.LabPlus;
  }

  static isLesson(offeringType: CourseType) {
    return offeringType === CourseType.Lesson;
  }

  static isTechOverview(offeringModality: Modality) {
    return offeringModality === Modality.TechOverview;
  }

  static isExam(offeringModality: Modality) {
    return offeringModality === Modality.Exam;
  }

  static isExamExperience(offeringModality: Modality, examType: ExamType) {
    return this.isExam(offeringModality) && examType === ExamType.Experience;
  }

  static isCompReview(
    offeringModality: Modality,
    offeringType: CourseType,
    subformat: CourseSubformat,
  ) {
    return (
      offeringModality === Modality.Course &&
      offeringType === CourseType.Lesson &&
      subformat === CourseSubformat.CompReview
    );
  }

  static getTitleWithFallback(entry: CatalogEntry, language: string): string {
    const langTitle = getCourseTitleByLanguage(entry.translations, language);

    // getCourseTitleByLanguage returns '' when translations are missing,
    // and undefined when they exist but none match the requested language
    return langTitle || entry.title || '';
  }

  /**
   * Resolves the offering-type key for a catalog entry
   * (entry-based cards only — no live-event / learning-path / premium overlays).
   */
  static resolveOfferingType(entry?: CatalogEntry): OfferingTypes | undefined {
    if (!entry) {
      return undefined;
    }

    if (this.isEarlyAccess(entry.code)) {
      return ModalityExtension.EarlyAccess;
    }

    if (
      entry.course_type &&
      entry.subformat &&
      this.isCompReview(entry.modality, entry.course_type, entry.subformat)
    ) {
      return ModalityExtension.CompReview;
    }

    if (entry.course_type && this.isLabPlus(entry.course_type)) {
      return ModalityExtension.LabPlus;
    }

    if (entry.course_type && this.isLesson(entry.course_type)) {
      return ModalityExtension.Lesson;
    }

    if (
      entry.exam_type &&
      this.isExamExperience(entry.modality, entry.exam_type)
    ) {
      return ModalityExtension.ExamExperience;
    }

    return entry.modality as OfferingTypes;
  }

  /** Modalities / extensions that open content via the `courses:page` route. */
  static supportsCoursePageDeepLink(entry?: CatalogEntry): boolean {
    if (!entry) {
      return false;
    }

    const offeringType = this.resolveOfferingType(entry);

    return (
      offeringType === Modality.Course ||
      offeringType === ModalityExtension.Lesson ||
      offeringType === ModalityExtension.LabPlus ||
      offeringType === ModalityExtension.CompReview ||
      offeringType === ModalityExtension.EarlyAccess ||
      offeringType === ModalityExtension.LiveVirtualClass
    );
  }

  private static courseSlug(entry: CatalogEntry) {
    return `${entry.code}-${entry.version}`;
  }

  private static courseViewOrOverview(
    entry: CatalogEntry,
    hasProgress: boolean,
  ): CatalogEntryRoute {
    return {
      routeName: hasProgress ? 'courses:view' : 'courses:overview',
      routeParams: { course: this.courseSlug(entry) },
    };
  }

  /**
   * Pure route resolution for a catalog entry (no router required).
   * Shared by OfferingCard primary actions and Search result links.
   */
  static getCatalogEntryRoute(
    entry?: CatalogEntry,
    options: GetCatalogEntryRouteOptions = {},
  ): CatalogEntryRoute | null {
    if (!entry) {
      return null;
    }

    const { hasProgress = false, pageTag } = options;

    if (pageTag && this.supportsCoursePageDeepLink(entry)) {
      return {
        routeName: 'courses:page',
        routeParams: {
          course: this.courseSlug(entry),
          page: pageTag,
        },
      };
    }

    const offeringType = this.resolveOfferingType(entry);

    switch (offeringType) {
      case Modality.Course:
      case ModalityExtension.LiveVirtualClass: {
        const isBundledCourse = entry.format === CourseFormat.Bundle;
        const openOverviewFirst = !hasProgress || isBundledCourse;
        return {
          routeName: openOverviewFirst ? 'courses:overview' : 'courses:view',
          routeParams: { course: this.courseSlug(entry) },
        };
      }
      case ModalityExtension.Lesson:
      case ModalityExtension.LabPlus:
      case ModalityExtension.CompReview:
      case ModalityExtension.EarlyAccess:
        return this.courseViewOrOverview(entry, hasProgress);
      case Modality.Exam:
      case ModalityExtension.ExamExperience:
        return {
          routeName: 'exams:view',
          routeParams: { slug: entry.code },
        };
      case Modality.ExpertSeminar:
        return {
          routeName: 'expertSeminars:view',
          routeParams: { slug: this.courseSlug(entry) },
        };
      case Modality.TechOverview:
        return {
          routeName: hasProgress
            ? 'tech-overview:view'
            : 'tech-overview:overview',
          routeParams: { slug: entry.slug },
        };
      default:
        return null;
    }
  }

  getCatalogEntryLink(
    entry?: CatalogEntry,
    options: GetCatalogEntryRouteOptions = {},
  ): CatalogEntryLink | null {
    const route = CatalogStore.getCatalogEntryRoute(entry, options);

    if (!route) {
      return null;
    }

    const { routerStore } = this.rootStore;
    const { routeName, routeParams } = route;

    return {
      routeName,
      routeParams,
      href: routerStore.router.buildUrl(routeName, routeParams),
      navigate: () => routerStore.navigate(routeName, routeParams),
    };
  }
}

export default CatalogStore;
